"use client";

import { useSearchParams } from "next/navigation";
import Link from "next/link";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/24/outline";

type CompaniesPaginationProps = {
  currentPage: number;
  lastPage: number;
};

export default function CompaniesPagination({
  currentPage,
  lastPage,
}: CompaniesPaginationProps) {
  const searchParams = useSearchParams();

  if (lastPage <= 1) {
    return null;
  }

  const prevDisabled = currentPage <= 1;
  const nextDisabled = currentPage >= lastPage;

  // Build page href preserving current filters
  const buildPageHref = (page: number) => {
    const safePage = Math.max(1, page);
    const params = new URLSearchParams(searchParams.toString());

    if (safePage === 1) {
      params.delete("page");
    } else {
      params.set("page", String(safePage));
    }

    const queryString = params.toString();
    return queryString ? `/empresas?${queryString}` : "/empresas";
  };

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages: (number | "...")[] = [];
    const showEllipsisStart = currentPage > 3;
    const showEllipsisEnd = currentPage < lastPage - 2;

    if (lastPage <= 5) {
      // Show all pages if 5 or less
      for (let i = 1; i <= lastPage; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (showEllipsisStart) {
        pages.push("...");
      }

      // Show pages around current
      for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(lastPage - 1, currentPage + 1);
        i++
      ) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (showEllipsisEnd) {
        pages.push("...");
      }

      // Always show last page
      if (!pages.includes(lastPage)) {
        pages.push(lastPage);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <nav className="flex flex-col items-center gap-4" aria-label="Paginação">
      <div className="flex items-center gap-1 sm:gap-2">
        {/* First Page */}
        {currentPage > 2 && lastPage > 3 && (
          <Link
            href={buildPageHref(1)}
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-white border border-gray-200 text-gray-600 hover:bg-mygreen hover:text-white hover:border-mygreen transition-all shadow-sm"
            aria-label="Primeira página"
          >
            <ChevronDoubleLeftIcon className="w-4 h-4" />
          </Link>
        )}

        {/* Previous Page */}
        {prevDisabled ? (
          <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-100 text-gray-400 cursor-not-allowed">
            <ChevronLeftIcon className="w-5 h-5" />
          </span>
        ) : (
          <Link
            href={buildPageHref(currentPage - 1)}
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-white border border-gray-200 text-gray-600 hover:bg-mygreen hover:text-white hover:border-mygreen transition-all shadow-sm"
            aria-label="Página anterior"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </Link>
        )}

        {/* Page Numbers */}
        <div className="flex items-center gap-1">
          {pageNumbers.map((page, index) =>
            page === "..." ? (
              <span
                key={`ellipsis-${index}`}
                className="flex items-center justify-center w-10 h-10 text-gray-400"
              >
                ···
              </span>
            ) : (
              <Link
                key={`page-${page}`}
                href={buildPageHref(page as number)}
                className={`flex items-center justify-center w-10 h-10 rounded-xl font-semibold transition-all ${
                  page === currentPage
                    ? "bg-mygreen text-white shadow-lg shadow-mygreen/30"
                    : "bg-white border border-gray-200 text-gray-700 hover:bg-mygreen hover:text-white hover:border-mygreen shadow-sm"
                }`}
                aria-current={page === currentPage ? "page" : undefined}
              >
                {page}
              </Link>
            )
          )}
        </div>

        {/* Next Page */}
        {nextDisabled ? (
          <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-100 text-gray-400 cursor-not-allowed">
            <ChevronRightIcon className="w-5 h-5" />
          </span>
        ) : (
          <Link
            href={buildPageHref(currentPage + 1)}
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-white border border-gray-200 text-gray-600 hover:bg-mygreen hover:text-white hover:border-mygreen transition-all shadow-sm"
            aria-label="Próxima página"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </Link>
        )}

        {/* Last Page */}
        {currentPage < lastPage - 1 && lastPage > 3 && (
          <Link
            href={buildPageHref(lastPage)}
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-white border border-gray-200 text-gray-600 hover:bg-mygreen hover:text-white hover:border-mygreen transition-all shadow-sm"
            aria-label="Última página"
          >
            <ChevronDoubleRightIcon className="w-4 h-4" />
          </Link>
        )}
      </div>

      {/* Page Info */}
      <p className="text-sm text-gray-500">
        Página{" "}
        <span className="font-semibold text-gray-700">{currentPage}</span> de{" "}
        <span className="font-semibold text-gray-700">{lastPage}</span>
      </p>
    </nav>
  );
}
